import React, { useCallback, useEffect, useState } from 'react';
import { Tag, Tooltip } from 'antd';

import { describeQuery } from '../../utils/filterSummary';
import {
	forgetRecentFilter,
	readRecentFilters,
} from '../../utils/recentFilters';

const TRUNCATE_AT = 40;

const truncate = text => (text.length > TRUNCATE_AT ? `${text.slice(0, TRUNCATE_AT - 1)}…` : text);

/**
 * The last five filters applied to this list, at the top of the builder.
 *
 * A filter you built two minutes ago is the one you are most likely to want
 * next, and rebuilding it row by row is the tax the builder charged for not
 * remembering. These are not saved views — no name, no server, no management
 * screen — so they read as what they are: the query in its own words
 * ("Status is Open · Due: This week"), one click to re-apply, an ✕ to forget.
 *
 * They live in this browser (see `utils/recentFilters.js`), and one that has
 * since been saved as a view is pruned: the saved-view chip beside this strip
 * already offers it, with a name.
 */
export default function RecentFilters({
	scopeKey,
	savedQueries = [],
	fields = [],
	activeQuery = '',
	refreshToken = 0,
	onApply,
}) {
	const [queries, setQueries] = useState([]);

	useEffect(() => {
		setQueries(scopeKey ? readRecentFilters(scopeKey, { savedQueries }) : []);
		// `savedQueries` is a fresh array on most renders; its CONTENT is what
		// matters, and it is re-read on every open anyway (refreshToken).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scopeKey, refreshToken, savedQueries.length]);

	const forget = useCallback((query) => {
		forgetRecentFilter(scopeKey, query);
		// Re-read rather than trusting what the write returned: the stored list
		// still holds the entries a saved view has since claimed, and they must
		// not reappear because a neighbour was dismissed.
		setQueries(readRecentFilters(scopeKey, { savedQueries }));
	}, [scopeKey, savedQueries]);

	if (!scopeKey || !queries.length) return null;

	return (
		<div className="list-filter__recents">
			<div className="micro-label list-filter__recents-label">Recent</div>
			<div className="list-filter__recents-chips">
				{queries.map(query => {
					const words = describeQuery(query, fields) || query;
					return (
						<Tooltip key={query} title={words}>
							<Tag
								className={`list-filter__recent view-chip${query === activeQuery ? ' list-filter__recent--active' : ''}`}
								closable
								onClose={event => { event.preventDefault(); forget(query); }}
								onClick={() => onApply(query)}
							>
								{truncate(words)}
							</Tag>
						</Tooltip>
					);
				})}
			</div>
		</div>
	);
}
